/**
 * Adzuki.VoiceInput — dictation into a text field.
 *
 * Wraps the browser's Web Speech API, which is what every voice feature in CP
 * Maxx already uses (AI Cruise Search, Ask Maxx, Quick Actions). Nothing is
 * recorded or uploaded; the browser does the transcription and hands back text.
 *
 * Deliberately touches NO DOM. The caller owns its own button and listening
 * state, so this works the same from a jQuery page and from a Vue island —
 * which is why it is a helper rather than a fourth copy of the same code.
 *
 *     if (Adzuki.VoiceInput.isSupported()) {
 *         var mic = Adzuki.VoiceInput.create({
 *             getPrefix: function () { return app.draft; },
 *             onText   : function (text) { app.draft = text; },
 *             onStart  : function () { app.listening = true; },
 *             onEnd    : function () { app.listening = false; }
 *         });
 *     }
 *
 *     mic.toggle();
 */
(function VoiceInput($, Adzuki)
{
	"use strict";

	// Chrome and Edge expose it prefixed; Firefox does not expose it at all, so
	// isSupported() is also what hides the control on browsers that cannot do it.
	var Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;

	// The API fires onspeechend far too eagerly to use as "they finished", so
	// stopping is driven by a quiet period instead.
	var IDLE_MS = 5000;

	var voice = {};

	voice.isSupported = function isSupported()
	{
		return Boolean(Recognition);
	};

	/**
	 * @param  object options getPrefix, onText, onStart, onEnd, onError, idleMs
	 * @return object toggle, start, stop, isListening
	 */
	voice.create = function create(options)
	{
		var settings    = $.extend({ idleMs: IDLE_MS }, options || {});
		var recognition = null;
		var idleTimer   = null;

		// Whatever was already in the field when dictation began. Held so speech
		// APPENDS rather than replaces — the existing implementations overwrite
		// the whole field, which destroys anything already typed.
		var prefix = '';

		function callback(name, arg)
		{
			if (typeof settings[name] === 'function') {
				settings[name](arg);
			}
		}

		function resetIdleTimer()
		{
			window.clearTimeout(idleTimer);

			idleTimer = window.setTimeout(function _idle() {
				if (recognition) {
					recognition.stop();
				}
			}, settings.idleMs);
		}

		function finish()
		{
			window.clearTimeout(idleTimer);
			recognition = null;
			callback('onEnd');
		}

		function start()
		{
			if (recognition || !Recognition) {
				return;
			}

			prefix = (typeof settings.getPrefix === 'function') ? (settings.getPrefix() || '') : '';

			// Separate the dictation from existing text, but do not introduce a
			// leading space in an empty field.
			if (prefix !== '' && !(/\s$/).test(prefix)) {
				prefix += ' ';
			}

			recognition                 = new Recognition();
			recognition.continuous      = false;
			recognition.interimResults  = true;
			recognition.lang            = 'en-US';
			recognition.maxAlternatives = 1;

			recognition.onstart = function _onStart() {
				resetIdleTimer();
				callback('onStart');
			};

			recognition.onresult = function _onResult(event) {
				var transcript = '';
				var i;

				resetIdleTimer();

				for (i = event.resultIndex; i < event.results.length; ++i) {
					transcript += event.results[i][0].transcript;
				}

				if (transcript !== '') {
					callback('onText', prefix + transcript);
				}
			};

			recognition.onend = finish;

			recognition.onerror = function _onError(event) {
				var reason = (event && event.error) ? event.error : '';

				finish();

				// "aborted" and "no-speech" are the ordinary ways a session ends
				// — telling someone their microphone is muted because they said
				// nothing would be wrong and annoying.
				if (reason === 'aborted' || reason === 'no-speech') {
					return;
				}

				if (typeof settings.onError === 'function') {
					settings.onError(reason);
					return;
				}

				Adzuki.StatusMessage(
					'error',
					(reason === 'not-allowed' || reason === 'service-not-allowed')
						? 'Microphone access was blocked. Allow it for this site in your browser settings and try again.'
						: 'Ensure that your microphone is turned on and/or your device is not muted.',
					'Speech Recognition Error'
				);
			};

			recognition.start();
		}

		function stop()
		{
			if (!recognition) {
				return;
			}

			// stop() delivers whatever it already has; abort() would discard it.
			recognition.stop();
		}

		return {
			isListening: function isListening() {
				return recognition !== null;
			},
			start : start,
			stop  : stop,
			toggle: function toggle() {
				if (recognition) {
					stop();
				} else {
					start();
				}
			},
		};
	};

	Adzuki.VoiceInput = voice;
}(jQuery, window.Adzuki || {}));
